<!-- Hero / Breadcrumb -->
<section class="terms-hero">
  <div class="container hero-inner">
    <div class="row align-items-center g-5">
      <div class="col-lg-7" data-aos="fade-up" data-aos-duration="1000" data-aos-once="true">
        <span class="breadcrumb-pill">
          <i class="ti ti-file-text"></i>
          Terms &amp; Conditions
        </span>
        <h1 class="mt-3 mb-3">
          A clear agreement between
          <span class="grad-text">you &amp; AVR HRMS</span>
        </h1>
        <p class="text-gray-9 mb-4" style="max-width: 560px">
          These Terms govern your use of our HRMS platform, services, and website. Plain language,
          fair rules — so you always know where you stand.
        </p>
        <div class="d-flex flex-wrap gap-2">
          <a href="#summary" class="btn btn-primary-gradient rounded btn-sm align-items-center">
            <i class="ti ti-list-check ms-1 me-2"></i>Quick Summary
            <i class="ti ti-arrow-right ms-2"></i>
          </a>
          <a href="#faq" class="btn btn-secondary rounded btn-sm align-items-center">
            <i class="ti ti-help-circle me-2"></i>Read FAQs
          </a>
        </div>
        <div class="mt-4 d-flex flex-wrap gap-3 text-gray-9" style="font-size: 14px">
          <span class="updated-chip"
            ><i class="ti ti-calendar-event"></i> Last updated: Jan 2026</span
          >
          <span><i class="ti ti-scale text-primary me-1"></i> Fair &amp; transparent</span>
          <span><i class="ti ti-language text-primary me-1"></i> Plain English</span>
        </div>
      </div>
      <div
        class="col-lg-5 hero-art"
        data-aos="zoom-in"
        data-aos-duration="1000"
        data-aos-once="true"
      >
        <div class="art-card text-center">
          <div class="art-icon"><i class="ti ti-file-certificate"></i></div>
          <h4 class="mb-2">Your Agreement, Simplified</h4>
          <p class="text-gray-9 mb-3">
            By using AVR HRMS, you agree to these Terms. We've written them to be
            <strong class="text-primary">readable, balanced, and honest</strong>.
          </p>
          <div class="badge-row">
            <span class="mini-badge"><i class="ti ti-shield-check"></i> Lawful Use</span>
            <span class="mini-badge"><i class="ti ti-credit-card"></i> Clear Billing</span>
            <span class="mini-badge"><i class="ti ti-user-shield"></i> Account Safety</span>
            <span class="mini-badge"><i class="ti ti-gavel"></i> Fair Disputes</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- /Hero -->

<!-- Highlight cards -->
<section class="section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-sparkles"></i> Key Principles</span>
      <h2 class="mt-3 fw-bold">Four pillars of our Terms</h2>
      <p class="text-gray-9 mx-auto" style="max-width: 640px">
        The values that shape every clause in this agreement.
      </p>
    </div>
    <div class="row g-4">
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon"><i class="ti ti-scale-outline"></i></span>
          <h5 class="fw-bold">Fair Use</h5>
          <p class="text-gray-9 mb-0">
            Use the platform lawfully and responsibly — for its intended HR purpose.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="200" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-1"><i class="ti ti-credit-card"></i></span>
          <h5 class="fw-bold">Transparent Billing</h5>
          <p class="text-gray-9 mb-0">
            No hidden fees. Clear plans, clear renewals, clear cancellations.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-2"><i class="ti ti-user-shield"></i></span>
          <h5 class="fw-bold">Account Security</h5>
          <p class="text-gray-9 mb-0">You safeguard your credentials; we safeguard the platform.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="400" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-3"><i class="ti ti-gavel"></i></span>
          <h5 class="fw-bold">Fair Disputes</h5>
          <p class="text-gray-9 mb-0">
            Issues are resolved transparently under applicable Indian law.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Quick summary -->
<section id="summary" class="terms-section-bg section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-list-check"></i> Quick Summary</span>
      <h2 class="mt-3 fw-bold">The Terms in 4 short cards</h2>
    </div>
    <div class="row g-4">
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">1</span>
          <i class="ti ti-user-plus data-ico"></i>
          <h5 class="fw-bold mt-2">Account</h5>
          <p class="text-gray-9 mb-0">
            Provide accurate info, keep your password safe, and you're responsible for activity on
            your account.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="200" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">2</span>
          <i class="ti ti-receipt-2 data-ico"></i>
          <h5 class="fw-bold mt-2">Subscription</h5>
          <p class="text-gray-9 mb-0">
            Plans renew automatically. Cancel anytime; refunds follow our Refund Policy.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">3</span>
          <i class="ti ti-license data-ico"></i>
          <h5 class="fw-bold mt-2">License</h5>
          <p class="text-gray-9 mb-0">
            We grant you a limited, non-exclusive right to use AVR HRMS for your business.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="400" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">4</span>
          <i class="ti ti-ban data-ico"></i>
          <h5 class="fw-bold mt-2">Restrictions</h5>
          <p class="text-gray-9 mb-0">
            No reverse engineering, abuse, illegal use, or reselling of the platform.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Allowed vs Not Allowed + Agreement visual -->
<section class="section-pad">
  <div class="container">
    <div class="row g-5 align-items-center">
      <div class="col-lg-6" data-aos="fade-right" data-aos-once="true">
        <span class="section-eyebrow"><i class="ti ti-checks"></i> Acceptable Use</span>
        <h3 class="mt-3 fw-bold">What you can &amp; can't do</h3>
        <p class="text-gray-9">
          To keep AVR HRMS reliable and safe for everyone, we ask all users to follow these simple
          ground rules.
        </p>

        <h6 class="fw-bold text-success mt-4 mb-2"><i class="ti ti-check me-1"></i> You CAN</h6>
        <div class="right-row">
          <span class="icon-tick"><i class="ti ti-check"></i></span>
          <div>
            <strong>Manage your team's HR data</strong>
            <p class="text-gray-9 mb-0">
              Attendance, payroll, leaves, performance — everything intended.
            </p>
          </div>
        </div>
        <div class="right-row">
          <span class="icon-tick"><i class="ti ti-check"></i></span>
          <div>
            <strong>Invite employees &amp; admins</strong>
            <p class="text-gray-9 mb-0">Within your purchased seat limits.</p>
          </div>
        </div>
        <div class="right-row">
          <span class="icon-tick"><i class="ti ti-check"></i></span>
          <div>
            <strong>Export your own data</strong>
            <p class="text-gray-9 mb-0">Your data is yours — download it anytime.</p>
          </div>
        </div>

        <h6 class="fw-bold text-danger mt-4 mb-2"><i class="ti ti-x me-1"></i> You CANNOT</h6>
        <div class="right-row">
          <span class="icon-cross"><i class="ti ti-x"></i></span>
          <div>
            <strong>Resell or sublicense</strong>
            <p class="text-gray-9 mb-0">The platform is licensed to your organization only.</p>
          </div>
        </div>
        <div class="right-row">
          <span class="icon-cross"><i class="ti ti-x"></i></span>
          <div>
            <strong>Reverse engineer or scrape</strong>
            <p class="text-gray-9 mb-0">No copying, decompiling, or automated extraction.</p>
          </div>
        </div>
        <div class="right-row">
          <span class="icon-cross"><i class="ti ti-x"></i></span>
          <div>
            <strong>Use for unlawful purposes</strong>
            <p class="text-gray-9 mb-0">Including spam, harassment, or storing illegal content.</p>
          </div>
        </div>
      </div>
      <div class="col-lg-6" data-aos="fade-left" data-aos-once="true">
        <div class="agreement-visual text-center">
          <span class="ring r1"></span>
          <span class="ring r2"></span>
          <div
            class="art-icon mx-auto"
            style="
              width: 96px;
              height: 96px;
              border-radius: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #6366f1, #8b5cf6);
              color: #fff;
              font-size: 46px;
              box-shadow: 0 18px 30px rgba(99, 102, 241, 0.35);
              margin-bottom: 18px;
            "
          >
            <i class="ti ti-writing-sign"></i>
          </div>
          <h4 class="fw-bold">A balanced agreement</h4>
          <p class="text-gray-9">
            Our Terms protect both sides. They explain how the service works, what we promise, and
            what we expect from you.
          </p>
          <div class="mt-3">
            <span class="sec-pill"><i class="ti ti-shield-check"></i> Lawful Use</span>
            <span class="sec-pill indigo"><i class="ti ti-license"></i> Limited License</span>
            <span class="sec-pill green"><i class="ti ti-credit-card"></i> Clear Billing</span>
            <span class="sec-pill"><i class="ti ti-refresh"></i> Auto-Renewal</span>
            <span class="sec-pill indigo"><i class="ti ti-gavel"></i> Indian Jurisdiction</span>
            <span class="sec-pill green"><i class="ti ti-clock"></i> 30-Day Notice</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Subscription, IP, Termination -->
<section class="section-pad" style="background: #fafbff">
  <div class="container">
    <div class="row g-5 align-items-start">
      <div class="col-lg-6" data-aos="fade-up" data-aos-once="true">
        <span class="section-eyebrow"
          ><i class="ti ti-receipt"></i> Subscription &amp; Billing</span
        >
        <h3 class="mt-3 fw-bold">How payments &amp; renewals work</h3>
        <div class="mt-3">
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-credit-card"></i></span>
            <div>
              <strong>Auto-renewal</strong>
              <p class="text-gray-9 mb-0">
                Plans renew at the end of each billing cycle unless cancelled.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-discount-2"></i></span>
            <div>
              <strong>Pricing changes</strong>
              <p class="text-gray-9 mb-0">
                We notify you at least 30 days before any price change takes effect.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-cash-banknote"></i></span>
            <div>
              <strong>Refunds</strong>
              <p class="text-gray-9 mb-0">
                Eligibility &amp; timelines are governed by our
                <a href="/refund-policy" class="text-primary fw-semibold">Refund Policy</a>.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-receipt-tax"></i></span>
            <div>
              <strong>Taxes</strong>
              <p class="text-gray-9 mb-0">
                Prices exclude applicable taxes (GST/VAT) which are added at checkout.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6" data-aos="fade-up" data-aos-delay="150" data-aos-once="true">
        <span class="section-eyebrow" style="background: #eef0ff; color: #6366f1">
          <i class="ti ti-copyright"></i>
          Intellectual Property &amp; Termination
        </span>
        <h3 class="mt-3 fw-bold">Ownership &amp; ending the service</h3>
        <div class="mt-3">
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-copyright"></i></span>
            <div>
              <strong>Our IP</strong>
              <p class="text-gray-9 mb-0">
                Code, design, brand, and trademarks remain the property of AVR HRMS.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-database"></i></span>
            <div>
              <strong>Your data</strong>
              <p class="text-gray-9 mb-0">
                You retain full ownership of all data you upload to the platform.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-door-exit"></i></span>
            <div>
              <strong>Termination by you</strong>
              <p class="text-gray-9 mb-0">
                Cancel anytime from your dashboard. Access continues until the cycle ends.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-alert-triangle"></i></span>
            <div>
              <strong>Termination by us</strong>
              <p class="text-gray-9 mb-0">
                We may suspend accounts for breach of these Terms after written notice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Acceptance & Updates Timeline -->
<section class="section-pad terms-section-bg">
  <div class="container">
    <div class="row g-5 align-items-center">
      <div class="col-lg-5" data-aos="fade-right" data-aos-once="true">
        <span class="section-eyebrow"><i class="ti ti-history"></i> How updates work</span>
        <h3 class="mt-3 fw-bold">Acceptance &amp; future changes</h3>
        <p class="text-gray-9">
          We may update these Terms to reflect new features or legal requirements. Here's the
          lifecycle of any change.
        </p>
        <a
          href="mailto:legal@avrhrms.com"
          class="btn btn-primary-gradient rounded btn-sm align-items-center mt-2"
        >
          <i class="ti ti-mail me-2"></i>
          Contact Legal Team
          <i class="ti ti-arrow-right ms-2"></i>
        </a>
      </div>
      <div class="col-lg-7" data-aos="fade-left" data-aos-once="true">
        <div class="terms-timeline">
          <div class="tl-item">
            <h6><i class="ti ti-edit me-2 text-primary"></i>Step 1 — Draft</h6>
            <p class="text-gray-9 mb-0">
              Our legal &amp; product team prepares the proposed change with plain-language
              explanations.
            </p>
          </div>
          <div class="tl-item">
            <h6><i class="ti ti-mail-share me-2 text-primary"></i>Step 2 — Notify (30 days)</h6>
            <p class="text-gray-9 mb-0">
              You receive an email and a banner inside your dashboard at least 30 days before
              changes take effect.
            </p>
          </div>
          <div class="tl-item">
            <h6><i class="ti ti-check me-2 text-primary"></i>Step 3 — Take effect</h6>
            <p class="text-gray-9 mb-0">
              Continued use after the effective date means you accept the updated Terms.
            </p>
          </div>
          <div class="tl-item">
            <h6><i class="ti ti-door-exit me-2 text-primary"></i>Step 4 — Opt-out</h6>
            <p class="text-gray-9 mb-0">
              If you don't agree, you can cancel before the effective date and request a pro-rata
              refund where eligible.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- FAQ -->
<section id="faq" class="section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-help"></i> FAQs</span>
      <h2 class="mt-3 fw-bold">Terms — frequently asked</h2>
    </div>
    <div class="row justify-content-center">
      <div class="col-lg-9" data-aos="fade-up" data-aos-once="true">
        <div class="accordion terms-faq" id="termsFaq">
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tfaq1"
              >
                <i class="ti ti-file-check me-2"></i>
                How do I accept these Terms?
              </button>
            </h2>
            <div id="tfaq1" class="accordion-collapse collapse show" data-bs-parent="#termsFaq">
              <div class="accordion-body">
                You accept the Terms by creating an account, subscribing to a plan, or simply using
                the AVR HRMS platform.
                <strong>Continued use</strong>
                means continued acceptance.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tfaq2"
              >
                <i class="ti ti-credit-card me-2"></i>
                Can I cancel my subscription anytime?
              </button>
            </h2>
            <div id="tfaq2" class="accordion-collapse collapse" data-bs-parent="#termsFaq">
              <div class="accordion-body">
                Yes. Cancel from your dashboard at any time — your access continues until the end of
                the current billing period. Refunds are governed by our
                <a href="/refund-policy" class="text-primary fw-semibold">Refund Policy</a>.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tfaq3"
              >
                <i class="ti ti-database me-2"></i>
                Who owns the data I upload?
              </button>
            </h2>
            <div id="tfaq3" class="accordion-collapse collapse" data-bs-parent="#termsFaq">
              <div class="accordion-body">
                <strong>You do.</strong>
                Your data is yours. We only process it to provide the service. You can export or
                delete it at any time.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tfaq4"
              >
                <i class="ti ti-shield-x me-2"></i>
                What happens if I violate the Terms?
              </button>
            </h2>
            <div id="tfaq4" class="accordion-collapse collapse" data-bs-parent="#termsFaq">
              <div class="accordion-body">
                We'll first reach out via email with details. For serious or repeated violations, we
                may
                <strong>suspend or terminate</strong>
                the account after providing reasonable written notice.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tfaq5"
              >
                <i class="ti ti-gavel me-2"></i>
                Which law governs these Terms?
              </button>
            </h2>
            <div id="tfaq5" class="accordion-collapse collapse" data-bs-parent="#termsFaq">
              <div class="accordion-body">
                These Terms are governed by the laws of
                <strong>India</strong>. Any disputes are subject to the exclusive jurisdiction of
                competent courts in our registered city.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tfaq6"
              >
                <i class="ti ti-bell me-2"></i>
                How will I know when the Terms change?
              </button>
            </h2>
            <div id="tfaq6" class="accordion-collapse collapse" data-bs-parent="#termsFaq">
              <div class="accordion-body">
                Material changes are announced via email and a prominent dashboard banner at least
                <strong>30 days</strong>
                before they take effect.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA -->
<section class="section-pad">
  <div class="container">
    <div class="terms-cta text-center" data-aos="zoom-in" data-aos-once="true">
      <i class="ti ti-scale" style="font-size: 54px; color: #ffb38a"></i>
      <h2 class="mt-3 mb-2" style="color: #ffffff">Need clarity on the Terms?</h2>
      <p class="mb-4" style="opacity: 0.85; max-width: 560px; margin-inline: auto">
        Our Legal &amp; Support team is happy to help you understand any clause — just reach out, no
        jargon, no delays.
      </p>
      <div class="d-flex flex-wrap gap-2 justify-content-center">
        <a
          href="mailto:legal@avrhrms.com"
          class="btn btn-primary-gradient rounded btn-sm align-items-center"
        >
          <i class="ti ti-mail me-2"></i>
          Email Legal Team
          <i class="ti ti-arrow-right ms-2"></i>
        </a>
        <a
          href="https://wa.me/918107011186"
          target="_blank"
          class="btn btn-secondary rounded btn-sm align-items-center"
        >
          <i class="fa-brands fa-whatsapp me-2"></i>
          Chat on WhatsApp
        </a>
      </div>
    </div>
  </div>
</section>
